import { Row } from "@circle-react-uikit/SkeletonLoader/components/Row";
import type { PropsWithChildren } from "react";

import { Typography } from "@/react/components/shared/uikit/Typography";
import type { CheckoutContextProps } from "@/react/contexts/Paywalls/paywallCheckoutContext";
import { classNames } from "@/react/helpers/twMergeWithCN";

import { usePaywallCustomizationContext } from "../PaywallCustomizationProvider";

interface DetailedProps {
  /**
   * Merged over the defaults. The fixed `md:w-[768px]` keys off the browser
   * viewport, so a surface that simulates a narrower width (the builder canvas)
   * overrides it rather than inheriting a column wider than its own frame.
   */
  className?: string;
  paywall: CheckoutContextProps["paywall"];
}

export const Detailed = Object.assign(
  ({ children, className, paywall }: PropsWithChildren<DetailedProps>) => {
    const { paywallCustomization } = usePaywallCustomizationContext();
    const detailedProperties = paywallCustomization?.detailed_properties;

    return (
      <div
        className={classNames(
          "flex flex-col md:w-[768px] items-center gap-y-5",
          className,
        )}
      >
        <div className="flex flex-col gap-y-2.5 text-center">
          <Typography.TitleLg data-testid="paywall-detailed-title">
            {detailedProperties?.title || paywall.display_name}
          </Typography.TitleLg>
          <Typography.BodyMd
            color="text-secondary"
            data-testid="paywall-detailed-description"
          >
            <span className="whitespace-pre-wrap">
              {detailedProperties?.description || paywall.description}
            </span>
          </Typography.BodyMd>
        </div>
        {children}
      </div>
    );
  },
  {
    Skeleton: () => (
      <div className="flex flex-col w-full md:w-[768px] items-center gap-y-5">
        <div className="flex flex-col gap-y-2.5 text-center w-full items-center">
          <Row half x2l />
          <Row full xxl />
        </div>
      </div>
    ),
  },
);
